import React from 'react';
import './TermsOfService.css';

interface TermsOfServiceProps {
  onClose?: () => void;
  isModal?: boolean;
}

export const TermsOfService: React.FC<TermsOfServiceProps> = ({ onClose, isModal = false }) => {
  const content = (
    <div className={`terms-of-service-content ${isModal ? 'modal-content' : ''}`}>
      {isModal && (
        <div className="terms-of-service-header">
          <h1>Terms of Service</h1>
          <button onClick={onClose} className="close-button" aria-label="Close">
            ×
          </button>
        </div>
      )}
      
      {!isModal && <h1>Terms of Service</h1>}
      
      <div className="last-updated">
        <strong>Last Updated:</strong> December 4, 2025
      </div>

      {/* Zero Tolerance Policy - Prominently displayed at top */}
      <section className="zero-tolerance-section">
        <h2>⚠️ ZERO TOLERANCE POLICY</h2>
        <p>
          Unreleased has a strict zero tolerance policy for objectionable content and abusive users. 
          Any user who violates our content guidelines will be permanently banned from the platform 
          without warning. This includes, but is not limited to:
        </p>
        <ul>
          <li>Hate speech, discrimination, or harassment of any kind</li>
          <li>Sexually explicit or pornographic content</li>
          <li>Content that promotes violence or terrorism</li>
          <li>Bullying, threats, or intimidation of other users</li>
          <li>Copyright infringement or stolen content</li>
          <li>Spam, scams, or fraudulent behavior</li>
        </ul>
        <p>
          We actively monitor content and respond to user reports. Violations will result in 
          immediate account termination and may be reported to law enforcement where applicable.
        </p>
      </section>

      <section>
        <h2>1. Acceptance of Terms</h2>
        <p>By accessing and using Unreleased ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. By creating an account, you confirm that you have read, understood, and agree to abide by these Terms of Service, including our Zero Tolerance Policy for objectionable content and abusive behavior.</p>
      </section>

      <section>
        <h2>2. Description of Service</h2>
        <p>Unreleased is a music feedback platform that allows users to upload music tracks and receive feedback from other users in the community.</p>
      </section>

      <section>
        <h2>3. User Accounts</h2>
        <p>To access certain features of the Service, you must register for an account. When you register for an account, you must provide information that is accurate, complete, and current at all times.</p>
        
        <h3>3.1 Account Security</h3>
        <p>You are responsible for safeguarding the password and for ensuring the security of your account. You agree not to disclose your password to any third party.</p>
        
        <h3>3.2 Account Termination</h3>
        <p>We reserve the right to suspend or terminate your account at any time for violations of these terms.</p>
      </section>

      <section>
        <h2>4. User Content</h2>
        
        <h3>4.1 Content Ownership</h3>
        <p>You retain all rights to any content you submit, post, or display on or through the Service. By submitting content, you grant us a non-exclusive license to use, display, and distribute your content on the platform.</p>
        
        <h3>4.2 Prohibited Content (Zero Tolerance)</h3>
        <p>The following types of content are strictly prohibited and will result in immediate account termination:</p>
        <ul>
          <li><strong>Hate Speech & Discrimination:</strong> Content that attacks, demeans, or incites violence against individuals or groups based on race, ethnicity, national origin, religion, sex, gender identity, sexual orientation, disability, or any other protected characteristic</li>
          <li><strong>Violence & Threats:</strong> Content that threatens, promotes, or glorifies violence, terrorism, or physical harm against any person or group</li>
          <li><strong>Harassment & Bullying:</strong> Content intended to harass, bully, intimidate, or abuse other users</li>
          <li><strong>Sexual Content:</strong> Sexually explicit, pornographic, or obscene material</li>
          <li><strong>Illegal Content:</strong> Content that promotes illegal activities, drug use, or violates any applicable law</li>
          <li><strong>Copyright Infringement:</strong> Content that violates third-party copyrights, trademarks, or other intellectual property rights</li>
          <li><strong>Spam & Fraud:</strong> Spam, scams, phishing, or misleading content</li>
          <li><strong>Malware:</strong> Content containing viruses, malware, or malicious code</li>
        </ul>
        
        <h3>4.3 Content Moderation</h3>
        <p>We actively moderate content and investigate all user reports. Users can report objectionable content or abusive behavior using the report feature available throughout the app. Our moderation team reviews all reports and takes swift action against violations.</p>
        
        <h3>4.4 Content Removal & Account Termination</h3>
        <p>We reserve the right to remove any content that violates these terms without notice. Serious or repeated violations will result in permanent account termination. We may also report illegal content to appropriate law enforcement authorities.</p>
      </section>

      <section>
        <h2>5. Feedback System</h2>
        <p>Our platform operates on a karma-based feedback system where users earn karma points by providing constructive feedback to others.</p>
        
        <h3>5.1 Feedback Guidelines</h3>
        <p>When providing feedback, users should:</p>
        <ul>
          <li>Be constructive and respectful</li>
          <li>Provide specific, actionable suggestions</li>
          <li>Focus on the music rather than personal attacks</li>
        </ul>
        
        <h3>5.2 Karma System</h3>
        <p>Karma points are awarded for quality feedback and may be used to access premium features. Karma points have no monetary value.</p>
      </section>

      <section>
        <h2>6. User Conduct (Zero Tolerance)</h2>
        <p>We have zero tolerance for abusive users. The following behaviors are strictly prohibited and will result in immediate permanent ban:</p>
        <ul>
          <li>Harassing, threatening, or bullying other users</li>
          <li>Sending unsolicited offensive messages or feedback</li>
          <li>Creating fake accounts to circumvent bans</li>
          <li>Impersonating other users or public figures</li>
          <li>Organizing or participating in targeted harassment campaigns</li>
          <li>Doxxing or sharing private information about other users</li>
          <li>Any form of discrimination or hate-based conduct</li>
        </ul>
        <p>Users who engage in abusive behavior will be permanently banned and their accounts deleted. We may also take legal action where appropriate.</p>
      </section>

      <section>
        <h2>7. Reporting Violations</h2>
        <p>We encourage users to report any content or behavior that violates these terms. You can report content using the flag/report button available on tracks and feedback throughout the app.</p>
        <p>All reports are reviewed by our moderation team within 24 hours. We take every report seriously and will take appropriate action, which may include content removal, warnings, or account termination.</p>
        <p>For urgent matters or to report serious violations, contact us directly at: <a href="mailto:admin@unreleasedtracks.com">admin@unreleasedtracks.com</a></p>
      </section>

      <section>
        <h2>8. Prohibited Uses</h2>
        <p>You may not use our Service:</p>
        <ul>
          <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
          <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
          <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
          <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
          <li>To submit false or misleading information</li>
          <li>To upload or transmit viruses or any other type of malicious code</li>
        </ul>
      </section>

      <section>
        <h2>9. Disclaimer</h2>
        <p>The information on this platform is provided on an "as is" basis. To the fullest extent permitted by law, we exclude all representations, warranties, and conditions relating to our service and the use of this platform.</p>
      </section>

      <section>
        <h2>10. Limitation of Liability</h2>
        <p>In no event shall Unreleased, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages.</p>
      </section>

      <section>
        <h2>11. Email Communications</h2>
        <p>By using our Service and creating an account, you agree to receive email communications from us. These communications may include:</p>
        <ul>
          <li>Welcome emails when you join the platform</li>
          <li>Notifications about feedback received on your tracks</li>
          <li>Updates about your karma points and account activity</li>
          <li>Service announcements and platform updates</li>
          <li>Promotional content and newsletters (which you may opt out of at any time)</li>
        </ul>
        <p>You may manage your email preferences in your account settings or unsubscribe from promotional emails by following the unsubscribe link in any email we send.</p>
      </section>

      <section>
        <h2>12. Privacy Policy</h2>
        <p>Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your information when you use our Service.</p>
      </section>

      <section>
        <h2>13. Changes to Terms</h2>
        <p>We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.</p>
      </section>

      <section>
        <h2>14. Contact Information</h2>
        <div className="contact-info">
          <p>If you have any questions about these Terms of Service, please contact us:</p>
          <p><strong>Email:</strong> <a href="mailto:admin@unreleasedtracks.com">admin@unreleasedtracks.com</a></p>
        </div>
      </section>
    </div>
  );

  if (isModal) {
    return (
      <div className="terms-of-service-modal-overlay" onClick={onClose}>
        <div className="terms-of-service-modal" onClick={(e) => e.stopPropagation()}>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="terms-of-service-container">
      {content}
    </div>
  );
};

export default TermsOfService;